/// <amd-module name="TFS/Views/ProjectOverviewPage/AboutProjectPanel/AboutProjectPanel" />
import * as React from "react";

import { format } from "VSS/Core/Util/String";
import { FormItem } from "VSSUI/FormItem";
import { TextField } from "VSSUI/TextField";
import { css } from "VSSUI/Util";

import { LegacyComponent } from "VSS/Legacy/Legacy";
import { RegionComponent, VssComponent } from "VSS/Platform/Layout";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { IButtonProps } from "VSSUI/Button";
import { ContentSize } from "VSSUI/Callout";
import { Panel } from "VSSUI/Panel";

import { AboutProjectPanelOverlay } from "TFS/Views/ProjectOverviewPage/AboutProjectPanel/AboutProjectPanelOverlay";
import * as Resources from "TFS/Views/ProjectOverviewPage/AboutProjectPanel/Resources";
import { TelemetryConstants } from "TFS/Views/ProjectOverviewPage/Constants";
import { PanelDisplayMode, PanelState } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Components/AboutProject/AboutProject";
import { ContributedAboutRegion } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Components/AboutProject/PanelAboutRegion/ContributedAboutRegion";
import {
    ComponentRegionNames,
    MaxDescriptionLength,
    OverviewTabTelemetryConstants,
    ProjectTagsConstants
} from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Constants";
import { SourceRepositoryTypes, TagInfo } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Models";
import * as PageResources from "TFS/Views/ProjectOverviewPage/Resources";

export interface IAboutProjectPanelProps {
    panelState: PanelState;
    showEditOption: boolean;
    projectName: string;
    allProjectTagsInCollection: string[];
    onSaveClick: () => void;
    onEditClick: () => void;
    onCancelClick: () => void;
    onOverlayCtaButtonClick: () => void;
    onDescriptionChanged: (description: string) => void;
    onTagListChanged: (tags: string[], errorMessage: string, invalidTag: string) => void;
    onAboutSourceChanged: (repoType?: SourceRepositoryTypes, repoId?: string) => void;
}

export class AboutProjectPanel extends VssComponent<IAboutProjectPanelProps> {
    private _textField = React.createRef<TextField>();

    public componentDidMount(): void {
        super.componentDidMount();

        this._focusTextField();
    }

    public componentDidUpdate(prevProps: IAboutProjectPanelProps): void {
        super.componentDidUpdate(prevProps, {});
        if (
            prevProps.panelState.displayMode !== PanelDisplayMode.showInEditMode &&
            this.props.panelState.displayMode === PanelDisplayMode.showInEditMode
        ) {
            this._focusTextField();
        }
    }

    public render(): JSX.Element | null {
        const showInEditMode = this._showInEditMode;
        const tags: TagInfo[] = (this.props.panelState.tags || []).map(tagName => {
            return {
                text: tagName,
                tagItemAriaLabel: format(PageResources.Tag_ItemNameAriaLabel, tagName),
                deleteButtonAriaLabel: format(PageResources.Tag_DeleteButtonAriaLabel, tagName),
                isFocusable: showInEditMode
            };
        });

        let autoCompleteSuggestedTagItems: string[] = [];
        const shouldRenderTags = showInEditMode || tags.length > 0;
        const descriptionInputClassName = "about-project-description-input";

        if (shouldRenderTags) {
            if (showInEditMode && this.props.allProjectTagsInCollection.length > 0) {
                autoCompleteSuggestedTagItems = this.props.allProjectTagsInCollection.slice();

                this.props.panelState.tags.forEach(tag => {
                    autoCompleteSuggestedTagItems = autoCompleteSuggestedTagItems.filter(value => value.toLowerCase() !== tag.toLowerCase());
                });
            }
        }

        const footerButtons: IButtonProps[] = [];
        if (this.props.showEditOption && this.props.panelState.displayMode === PanelDisplayMode.showInReadMode) {
            footerButtons.push({
                onClick: this.props.onEditClick,
                primary: true,
                text: Resources.AboutProjectEditButtonText
            });
        }
        if (this._showInEditMode) {
            footerButtons.push({
                className: "about-project-panel-button cancel-changes-button",
                disabled: this._disabled,
                onClick: this._onCancelButtonClick,
                text: Resources.AboutProjectCancelButtonText
            });
            footerButtons.push({
                className: "about-project-panel-button save-changes-button",
                disabled: this._disabled || this._hasError(),
                onClick: this._onSaveButtonClick,
                primary: true,
                text: Resources.AboutProjectSaveButtonText
            });
        }

        return this.props.panelState.displayMode !== PanelDisplayMode.hide ? (
            <Panel
                className={"about-project-edit-panel"}
                titleProps={{ text: PageResources.AboutProjectTitle }}
                footerButtonProps={footerButtons}
                onDismiss={this._onDismissButtonClick}
                defaultActiveElement={descriptionInputClassName}
                size={ContentSize.Large}
            >
                <div aria-live="assertive" aria-atomic="true">
                    {this._disabled && (
                        <AboutProjectPanelOverlay
                            showSaving={this.props.panelState.displayMode === PanelDisplayMode.showSaving}
                            descriptionError={this.props.panelState.panelError.descriptionError}
                            onCtaButtonClick={this.props.onOverlayCtaButtonClick}
                        />
                    )}
                </div>
                {
                    <div className={css("project-description-content flex-grow", this._showInEditMode ? "editmode" : "readmode")}>
                        {this.props.panelState.displayMode === PanelDisplayMode.showInReadMode && (
                            <RegionComponent componentRegion={ComponentRegionNames.ProjectOverviewLanguagesRegion} />
                        )}
                        <div className="description-section">
                            {showInEditMode ? (
                                <FormItem className="description-textfield" label={this._onRenderDescriptionLabel()}>
                                    <TextField
                                        ref={this._textField}
                                        maxLength={MaxDescriptionLength}
                                        inputClassName={descriptionInputClassName}
                                        multiline
                                        autoFocus
                                        onChange={this.onDescriptionChanged}
                                        placeholder={Resources.AboutProjectDescriptionInputWatermarkText}
                                        value={this.props.panelState.description || ""}
                                        disabled={this._disabled}
                                        resizable={false}
                                    />
                                </FormItem>
                            ) : (
                                <>
                                    <div className="title-xs">{Resources.AboutProjectPanelDescriptionHeading}</div>
                                    <div className="content">{this.props.panelState.description}</div>
                                </>
                            )}
                        </div>
                        {shouldRenderTags && (
                            <div className="tag-section">
                                <div className="title-xs">{PageResources.AboutProjectPanelTagsHeading}</div>
                                <div className="tags-component">
                                    <LegacyComponent
                                        wrappedType="TFS.ProjectOverview.TagListControl"
                                        modules={["ProjectOverview/Scripts/Shared/Components/TagListControl"]}
                                        autoCompleteSuggestedTagItems={autoCompleteSuggestedTagItems}
                                        tags={tags}
                                        editMode={showInEditMode}
                                        listAriaLabel={PageResources.ProjectTags_AriaLabel}
                                        tagListErrorMessage={this.props.panelState.panelError.tagErrorMessage}
                                        tagListCountLimit={ProjectTagsConstants.MaxCount}
                                        tagCharacterLimit={ProjectTagsConstants.CharacterLimit}
                                        tagListCountLimitExceededErrorMessage={format(
                                            PageResources.ProjectTags_MaxCountExceeded,
                                            ProjectTagsConstants.MaxCount
                                        )}
                                        tagListCountLimitReachedErrorMessage={format(
                                            PageResources.ProjectTags_MaxCountReached,
                                            ProjectTagsConstants.MaxCount
                                        )}
                                        tagCharacterLimitExceededErrorMessage={format(
                                            PageResources.ProjectTags_TagCharacterLimitExceeded,
                                            ProjectTagsConstants.CharacterLimit
                                        )}
                                        separators={ProjectTagsConstants.Separators}
                                        onTagListChanged={this.props.onTagListChanged}
                                    />
                                </div>
                            </div>
                        )}
                        <div className="about-section">
                            <ContributedAboutRegion
                                projectName={this.props.projectName}
                                disableEditing={this._disabled}
                                showError={this.props.showEditOption}
                                readMode={this.props.panelState.displayMode === PanelDisplayMode.showInReadMode}
                                selectedRepoId={this.props.panelState.repoId}
                                selectedRepoType={this.props.panelState.repoType}
                                onAboutSourceChanged={this.props.onAboutSourceChanged}
                            />
                        </div>
                    </div>
                }
            </Panel>
        ) : null;
    }

    private _onRenderDescriptionLabel = (): JSX.Element => {
        return <div className="title-xs">{Resources.AboutProjectPanelDescriptionHeading}</div>;
    };

    private _focusTextField(): void {
        // hack to put focus at the end of the text in the textfield for firefox and safari
        const { description } = this.props.panelState;
        const length = description ? description.length : 0;
        this._textField.current && this._textField.current.setSelectionRange(length, length);
    }

    private _hasError(): boolean {
        return (
            !!this.props.panelState.invalidTag ||
            (!!this.props.panelState.panelError.tagErrorMessage && this.props.panelState.tags.length > ProjectTagsConstants.MaxCount)
        );
    }

    private get _showInEditMode(): boolean {
        const { displayMode } = this.props.panelState;
        return (
            displayMode === PanelDisplayMode.showInEditMode ||
            displayMode === PanelDisplayMode.showSaving ||
            displayMode === PanelDisplayMode.showError
        );
    }

    private get _disabled(): boolean {
        const { displayMode } = this.props.panelState;
        return displayMode === PanelDisplayMode.showSaving || displayMode === PanelDisplayMode.showError;
    }

    private onDescriptionChanged = (event: React.ChangeEvent<HTMLInputElement>, newValue: string): void => {
        if (this.props.onDescriptionChanged) {
            this.props.onDescriptionChanged(newValue);
        }
    };

    private _onCancelButtonClick = (): void => {
        this._logHidePanelTelemetry("Cancel_Button");
        this.props.onCancelClick();
    };

    private _onSaveButtonClick = (): void => {
        this._logSavePanelTelemetry("Save_Button");
        this.props.onSaveClick();
    };

    private _onDismissButtonClick = (): void => {
        this._logHidePanelTelemetry("Cross_Button");
        this.props.onCancelClick();
    };

    private _logHidePanelTelemetry(source: string): void {
        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent(OverviewTabTelemetryConstants.Area, OverviewTabTelemetryConstants.AboutProjectPanel, {
            [TelemetryConstants.Event]: "Hide_Panel",
            Source: source
        });
    }

    private _logSavePanelTelemetry(source: string): void {
        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent(OverviewTabTelemetryConstants.Area, OverviewTabTelemetryConstants.AboutProjectPanel, {
            [TelemetryConstants.Event]: "Save_Panel",
            Source: source
        });
    }
}

VssComponent.register("TFS.ProjectOverview.AboutProjectPanel", AboutProjectPanel);
